import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, timeout, retry } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from 'src/app.config';

export interface MoratoriumRequest {
    zipCode: string;
    startDate: string;
    endDate: string;
}

export interface MoratoriumItem {
    zipCode: string;
    state: string;
    lob: string;
    moratoriumType: string;
    moratoriumName: string;
    startDate: string;
    endDate: string;
    action: string;
    updatedBy: string;
    updatedAt: string;
}

export interface MoratoriumResponse {
    zipCode: string;
    dateRange: {
        startDate: string;
        endDate: string;
    };
    totalCount: number;
    moratoriums: MoratoriumItem[];
}

@Injectable({
    providedIn: 'root'
})
export class MoratoriumService {
    private readonly TIMEOUT = 30000; // 30 seconds
    private readonly API_ENDPOINT: string;
    private readonly API_KEY: string;
    private readonly headers: HttpHeaders;

    constructor(
        private http: HttpClient,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {
        // Get API endpoint and key from config
        this.API_ENDPOINT = config.Moratorium_API
        this.API_KEY = config.apiKey;
        
        this.headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'x-api-key': this.API_KEY
        });
    }

    /**
     * Verify moratorium status for given zipcode and date range
     */
    verifyMoratorium(request: MoratoriumRequest): Observable<MoratoriumResponse> {
        if (!this.validateRequest(request)) {
            return throwError(() => new Error('Invalid request parameters'));
        }

        return this.http.post<MoratoriumResponse>(this.API_ENDPOINT, request, { 
            headers: this.headers 
        }).pipe(
            timeout(this.TIMEOUT),
            retry({
                count: 2,
                delay: (error, retryCount) => {
                    // Only retry on server errors (5xx)
                    if (error.status >= 500) {
                        return timer(retryCount * 1000); // 1s, 2s delay
                    }
                    throw error;
                }
            }),
            catchError(error => {
                console.error('Moratorium API error:', error);
                
                // Transform error for consistent handling
                let errorMessage = 'Failed to verify moratorium status';
                
                if (error.status === 400) {
                    errorMessage = 'Invalid request parameters';
                } else if (error.status === 401) {
                    errorMessage = 'Authentication failed';
                } else if (error.status === 403) {
                    errorMessage = 'Access denied';
                } else if (error.status === 404) {
                    errorMessage = 'Moratorium service not available';
                } else if (error.status >= 500) {
                    errorMessage = 'Server error. Please try again later.';
                } else if (error.name === 'TimeoutError') {
                    errorMessage = 'Request timeout. Please try again.';
                }

                return throwError(() => ({ ...error, message: errorMessage }));
            }),
        );
    }

    /**
     * Validate the moratorium request parameters
     */
    private validateRequest(request: MoratoriumRequest): boolean {
        if (!request) {
            console.error('Moratorium request is null or undefined');
            return false;
        }

        if (!request.zipCode || !/^\d{5}(-\d{4})?$/.test(request.zipCode)) {
            console.error('Invalid ZIP code format:', request.zipCode);
            return false;
        }

        if (!request.startDate || !this.isValidDate(request.startDate)) {
            console.error('Invalid start date:', request.startDate);
            return false;
        }

        if (!request.endDate || !this.isValidDate(request.endDate)) {
            console.error('Invalid end date:', request.endDate);
            return false;
        }

        if (new Date(request.startDate) >= new Date(request.endDate)) {
            console.error('Start date must be before end date');
            return false;
        }

        return true;
    }

    /**
     * Validate date format (YYYY-MM-DD)
     */
    private isValidDate(dateString: string): boolean {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(dateString)) {
            return false;
        }

        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date.getTime()) && 
               date.toISOString().startsWith(dateString);
    }
}